/**
 * `/leaderboard` — readable without a session.
 *
 * Outside `RequireAuth` deliberately, and the fetch it makes is the reason it can be: the
 * board is same-origin static JSON written by a background job, not an API call, so
 * a visitor can read it before deciding whether to sign in. That is also why the back link
 * points at `/` — which is the redirect, so a signed-in reader lands in their story and a
 * signed-out one lands at sign-in.
 *
 * **The empty state is the one that ships first.** No leaderboard object has ever been
 * written: the job fires on a death in a real story, and no real story has one yet
 * (the repo's `TODO.md` → Platform, "the leaderboard write"). So `null` here is the normal answer
 * today, and it must read as *nobody has died yet* rather than as a broken page.
 */
import { Link } from 'react-router';
import { describeApiError } from '@/api/errorMessages';
import { Leaderboard } from '@/features/leaderboard/Leaderboard';
import { useLeaderboard } from '@/features/leaderboard/board';
import { useT } from '@/i18n/context';
import { Page } from '@/ui/Page';

export function LeaderboardRoute() {
  const t = useT();
  const board = useLeaderboard();

  return (
    <Page title={t('ui.leaderboard.title')} back={{ to: '/', label: t('ui.nav.the_wasteland') }}>
      {board.isPending ? (
        <p className="text-fg-muted" style={{ fontSize: 'var(--text-action)' }}>
          {t('ui.common.loading')}
        </p>
      ) : board.error ? (
        <p className="text-fg-muted" style={{ fontSize: 'var(--text-action)' }}>
          {describeApiError(t, board.error)}
        </p>
      ) : board.data === null ? (
        <div className="flex flex-col gap-4">
          <p className="text-fg-muted" style={{ fontSize: 'var(--text-action)' }}>
            {t('ui.leaderboard.empty')}
          </p>
          <Link
            to="/stories"
            className="border-border text-fg-muted hover:border-accent hover:text-accent w-fit border px-4 py-2 font-mono transition-colors"
            style={{ fontSize: 'var(--text-action)' }}
          >
            {t('ui.nav.your_stories')}
          </Link>
        </div>
      ) : (
        <Leaderboard file={board.data} />
      )}
    </Page>
  );
}
